import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom"; // ← useNavigateを追加
import styled from "styled-components";
import { ClipLoader } from "react-spinners";
import { getUser, followUser, unfollowUser, getUserPosts } from "../api/user";
import { User } from "../types/User";
import { Post } from "../types/Post";
import PostCard from "../components/PostCard";
import { getMyProfile } from "../api/me";

const UserPage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate(); // ← 追加

  const [user, setUser] = useState<User | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<User | null>(null);

  const [postsLoading, setPostsLoading] = useState(true);
  const [postsError, setPostsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const meData = await getMyProfile();
        setMe(meData);
      } catch (err: unknown) {
        console.error(err);
      }
    };
    fetchMe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!username) return;

      try {
        setLoading(true);
        const userData = await getUser(username);
        setUser(userData);
      } catch (err: unknown) {
        setError("Failed to load user data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [username]);

  useEffect(() => {
    const fetchUserPosts = async () => {
      if (!user) return;
      try {
        setPostsLoading(true);
        const userPosts = await getUserPosts(user.scratch_name);
        setPosts(userPosts);
      } catch (err) {
        setPostsError("ポストの読み込み時にエラーが発生しました。");
        console.error(err);
      } finally {
        setPostsLoading(false);
      }
    };

    if (user) {
      fetchUserPosts();
    }
  }, [user]);

  const handleFollow = async () => {
    if (!user) return;
    try {
      await followUser(user.scratch_name);
      setUser((prevUser) =>
        prevUser
          ? {
              ...prevUser,
              follower_count: prevUser.follower_count + 1,
              is_following: true,
            }
          : null
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleUnfollow = async () => {
    if (!user) return;
    try {
      await unfollowUser(user.scratch_name);
      setUser((prevUser) =>
        prevUser
          ? {
              ...prevUser,
              follower_count: prevUser.follower_count - 1,
              is_following: false,
            }
          : null
      );
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <ClipLoader />
      </LoadingContainer>
    );
  }

  if (error) {
    return <p style={{ color: "red" }}>{error}</p>;
  }

  if (!user) {
    return <p>User not found.</p>;
  }

  const isMyPage = me && me.scratch_name === user.scratch_name;

  return (
    <UserPageWrapper>
      <UserProfile>
        <UserProfileHeader>
          <UserProfileIcon src={user.icon_url} alt="icon" />
          {isMyPage ? (
            <EditProfileButton>プロフィールを編集</EditProfileButton>
          ) : user.is_following ? (
            <UnfollowButton onClick={handleUnfollow}>フォロー中</UnfollowButton>
          ) : (
            <FollowButton onClick={handleFollow}>フォロー</FollowButton>
          )}
        </UserProfileHeader>
        <h2>{user.display_name || user.scratch_name}</h2>
        <p>@{user.scratch_name}</p>
        <p>{user.bio}</p>
        <FollowStats>
          <span onClick={() => navigate(`/${user.scratch_name}/following`)}>
            {user.following_count} フォロー
          </span>
          <span onClick={() => navigate(`/${user.scratch_name}/followers`)}>
            {user.follower_count} フォロワー
          </span>
        </FollowStats>
      </UserProfile>
      <UserPosts>
        <h3>投稿</h3>
        {postsLoading && <ClipLoader />}
        {postsError && <p style={{ color: "red" }}>{postsError}</p>}
        {posts.map((post) => (
          <PostCard key={post.id} post={post} />
        ))}
      </UserPosts>
    </UserPageWrapper>
  );
};

const UserPageWrapper = styled.div`
  max-width: 900px;
  margin: 30px auto;
`;

const UserProfile = styled.div`
  position: relative;
  margin-bottom: 20px;
  padding: 20px;
  border-bottom: 1px solid #e6ecf0;

  h2 {
    margin: 0;
  }

  p {
    margin: 5px 0;
  }
`;

const UserProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const UserProfileIcon = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
`;

const ButtonBase = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solidrgb(0, 0, 0);
  font-weight: bold;
  cursor: pointer;
`;

const FollowButton = styled(ButtonBase)`
  background-color: rgb(0, 0, 0);
  color: white;
`;

const UnfollowButton = styled(ButtonBase)`
  background-color: white;
  color: rgb(0, 0, 0);
`;

const EditProfileButton = styled(ButtonBase)`
  background-color: white;
  color: rgb(0, 0, 0);
`;

const FollowStats = styled.div`
  margin-top: 10px;

  span {
    margin-right: 15px;
    cursor: pointer;
    color: rgb(0, 0, 0);
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const UserPosts = styled.div`
  h3 {
    margin-bottom: 20px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

export default UserPage;
